import { visionLabApi } from '@/app/apiClient';
import { USER_DIRECTORY_PAGE_SIZE } from '@/features/user-management/config/directory';
import type {
  AssignmentsResponse,
  AuthorizationCatalog,
  CreatePermissionSetInput,
  InviteOrganizationMemberInput,
  InviteOrganizationMemberResult,
  PermissionSet,
  PermissionSetsResponse,
  ReplacePermissionSetInput,
  SaveAssignmentsInput,
  UserPage,
} from '@/features/user-management/types';
import type { ApiCallOptions } from '@/shared/api/client';
import { z } from 'zod';
const inviteOrganizationMemberInputSchema = z
  .object({
    organizationId: z.string().trim().min(1),
    email: z.string().trim().toLowerCase().email(),
    permissionSetId: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
    requestId: z.string().uuid(),
  })
  .strict();
export async function fetchUsers(
  params: { cursor?: string; q?: string } = {},
  options: ApiCallOptions = {},
): Promise<UserPage> {
  return visionLabApi.get<UserPage>('/authz/users', {
    ...options,
    searchParams: {
      limit: USER_DIRECTORY_PAGE_SIZE,
      cursor: params.cursor || undefined,
      q: params.q?.trim() || undefined,
    },
  });
}

export async function fetchCatalog(options: ApiCallOptions = {}): Promise<AuthorizationCatalog> {
  return visionLabApi.get<AuthorizationCatalog>('/authz/catalog', options);
}

export async function fetchPermissionSets(
  options: ApiCallOptions = {},
): Promise<PermissionSetsResponse> {
  return visionLabApi.get<PermissionSetsResponse>('/authz/permission-sets', options);
}

export async function createPermissionSet(
  input: CreatePermissionSetInput,
  options: ApiCallOptions = {},
): Promise<PermissionSet> {
  return visionLabApi.post<PermissionSet>('/authz/permission-sets', {
    ...options,
    json: input,
  });
}

export async function replacePermissionSet(
  id: string,
  input: ReplacePermissionSetInput,
  options: ApiCallOptions = {},
): Promise<PermissionSet> {
  return visionLabApi.put<PermissionSet>(`/authz/permission-sets/${encodeURIComponent(id)}`, {
    ...options,
    headers: { 'If-Match': `"permission-set-version-${input.expectedVersion}"` },
    json: {
      displayName: input.displayName,
      description: input.description,
      status: input.status,
      permissions: input.permissions,
      reason: input.reason,
    },
  });
}

export async function inviteOrganizationMember(
  input: InviteOrganizationMemberInput,
  options: ApiCallOptions = {},
): Promise<InviteOrganizationMemberResult> {
  const parsed = inviteOrganizationMemberInputSchema.parse(input);
  return visionLabApi.post<InviteOrganizationMemberResult>(
    `/organizations/${encodeURIComponent(parsed.organizationId)}/members`,
    {
      ...options,
      json: {
        email: parsed.email,
        permissionSetId: parsed.permissionSetId,
        requestId: parsed.requestId,
      },
    },
  );
}

export async function fetchAssignments(
  principalId: string,
  options: ApiCallOptions = {},
): Promise<AssignmentsResponse> {
  return visionLabApi.get<AssignmentsResponse>(
    `/authz/principals/${encodeURIComponent(principalId)}/assignments`,
    options,
  );
}

export async function replaceAssignments(
  input: SaveAssignmentsInput,
  options: ApiCallOptions = {},
): Promise<AssignmentsResponse> {
  return visionLabApi.put<AssignmentsResponse>(
    `/authz/principals/${encodeURIComponent(input.principalId)}/assignments`,
    {
      ...options,
      headers: { 'If-Match': `"principal-version-${input.expectedVersion}"` },
      json: {
        permissionSetAssignments: input.permissionSetAssignments,
        reason: input.reason,
      },
    },
  );
}
